// Copyright 2025, University of Maryland, All Rights Reserved
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui';
import { CADENCE_OPTIONS, DAILY_TIME_PREFIX } from '../api/types/scheduler';

interface CadenceFieldProps {
  value: string;
  onChange: (cadence: string) => void;
}

/**
 * Cadence selector with an optional time-of-day picker for the "daily" cadence.
 *
 * Picking Daily and a time produces a `daily@HH:MM` cadence, which the backend
 * runs once per day at that time (US/Eastern). Leaving the time blank keeps the
 * plain `daily` cadence (runs once every 24 hours).
 */
export function CadenceField({ value, onChange }: CadenceFieldProps) {
  const isTimedDaily = value.startsWith(DAILY_TIME_PREFIX);
  const base = isTimedDaily ? 'daily' : value;
  const time = isTimedDaily ? value.slice(DAILY_TIME_PREFIX.length) : '';

  const handleTimeChange = (next: string) => {
    onChange(next ? `${DAILY_TIME_PREFIX}${next}` : 'daily');
  };

  return (
    <div className="grid gap-2">
      <Label htmlFor="cadence">Cadence *</Label>
      <Select value={base} onValueChange={onChange}>
        <SelectTrigger id="cadence">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CADENCE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {base === 'daily' ? (
        <div className="grid gap-1">
          <Label htmlFor="daily-time" className="text-sm font-normal">
            Time (US/Eastern)
          </Label>
          <Input
            id="daily-time"
            type="time"
            value={time}
            onChange={(e) => handleTimeChange(e.target.value)}
            className="w-40"
          />
          <p className="text-sm text-gray-500">
            Optional: run daily at this time. Leave blank to run once every 24 hours.
          </p>
        </div>
      ) : (
        <p className="text-sm text-gray-500">How frequently the job should run</p>
      )}
    </div>
  );
}
